import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import bowtieService from './bowtiesService'

const bowties = JSON.parse(localStorage.getItem('bowties'))

//set initial state
const initialState = {
    bowties: bowties ? bowties : [],
    currentBowtie: null, // Added for singular bowtie
    isError: false,
    isSuccess: false,
    isLoading: false,
    message: ''
}

//create new bowtie
export const createBowtie = createAsyncThunk(
    'bowties/create', 
    async (bowtieData, thunkAPI) => {
    try {
        const token = thunkAPI.getState().auth.user.token
       return await bowtieService.createBowtie(bowtieData, token)
    } catch (error) {
        const message = (error.response && 
            error.response.data &&
            error.response.data.message) || 
            error.message || 
            error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

//get bowties (plural - for lists). Scoped to the active workspace
//('personal' or an org slug). Pass an explicit workspace to override.
export const getBowties = createAsyncThunk(
    'bowties/getAll',
    async (workspace, thunkAPI) => {
    try {
        const state = thunkAPI.getState()
        const token = state.auth.user.token
        const ws = workspace || state.org?.activeWorkspace || 'personal'
        return await bowtieService.getBowties(token, ws)
    } catch (error) {
        const message = (error.response &&
            error.response.data &&
            error.response.data.message) ||
            error.message ||
            error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

//move a personal bowtie into an organisation (one-way)
export const moveBowtieToOrg = createAsyncThunk(
    'bowties/moveToOrg',
    async ({ bowtieId, organisation }, thunkAPI) => {
    try {
        const token = thunkAPI.getState().auth.user.token
        return await bowtieService.moveBowtieToOrg({ bowtieId, organisation }, token)
    } catch (error) {
        const message = (error.response &&
            error.response.data &&
            error.response.data.message) ||
            error.message ||
            error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

//clone a bowtie — into the caller's personal workspace, or into an org
//(pass { bowtieId, organisation }). The source is untouched.
export const cloneBowtie = createAsyncThunk(
    'bowties/clone',
    async (arg, thunkAPI) => {
    try {
        const token = thunkAPI.getState().auth.user.token
        const bowtieId = typeof arg === 'string' ? arg : arg.bowtieId
        const organisation = typeof arg === 'string' ? null : arg.organisation || null
        return await bowtieService.cloneBowtie(bowtieId, token, organisation)
    } catch (error) {
        const message = (error.response &&
            error.response.data &&
            error.response.data.message) ||
            error.message ||
            error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

//get single bowtie (singular - for editor)
export const getBowtie = createAsyncThunk(
    'bowties/getOne', 
    async (id, thunkAPI) => {
    try {
        const token = thunkAPI.getState().auth.user.token
        return await bowtieService.getBowtie(id, token)
    } catch (error) {
        const message = (error.response && 
            error.response.data && 
            error.response.data.message) || 
            error.message || 
            error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

//delete bowtie
export const deleteBowtie = createAsyncThunk(
    'bowties/delete', 
    async (id, thunkAPI) => {
    try {
        const token = thunkAPI.getState().auth.user.token
       return await bowtieService.deleteBowtie(id, token)
    } catch (error) {
        const message = (error.response && 
            error.response.data && 
            error.response.data.message) || 
            error.message || 
            error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

//update bowtie
export const updateBowtie = createAsyncThunk(
    'bowties/update',
    async (bowtieData, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token
            return await bowtieService.updateBowtie(bowtieData, token)
        } catch (error) {
            const message = (error.response && 
                error.response.data && 
                error.response.data.message) || 
                error.message || 
                error.toString()
            return thunkAPI.rejectWithValue(message)
        }
    }
)

//Bowtie Slice
export const bowtieSlice = createSlice({
    name: 'bowties',
    initialState,
    reducers: {
        reset: (state) => initialState,
    },
    extraReducers: (builder) => {
            builder
            .addCase(createBowtie.pending, (state) => {
                state.isLoading = true
            })
            .addCase(createBowtie.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.bowties.push(action.payload)
            })
            .addCase(createBowtie.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })
            .addCase(getBowties.pending, (state) => {
                state.isLoading = true
                state.isError = false  // ✅ Reset error state
                state.message = ''     // ✅ Clear message
            })
            .addCase(getBowties.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false  // ✅ FIXED: Reset error state
                state.message = ''     // ✅ Clear any error message
                state.bowties = action.payload
            })
            .addCase(getBowties.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.isSuccess = false // ✅ FIXED: Reset success state
                state.message = action.payload
                state.bowties = []      // ✅ Clear data on error
            })
            .addCase(getBowtie.pending, (state) => {
                state.isLoading = true
                state.isError = false   // ✅ Reset error state
                state.message = ''      // ✅ Clear message
            })
            .addCase(getBowtie.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false   // ✅ FIXED: Reset error state
                state.message = ''      // ✅ Clear any error message
                state.currentBowtie = action.payload
            })
            .addCase(getBowtie.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.isSuccess = false // ✅ FIXED: Reset success state
                state.message = action.payload
                state.currentBowtie = null // ✅ Clear data on error
            })
            .addCase(deleteBowtie.pending, (state) => {
                state.isLoading = true
            })
            .addCase(deleteBowtie.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.bowties = state.bowties.filter(
                    (item) => item._id !== action.payload._id)
                // Clear current bowtie if it was deleted
                if (state.currentBowtie && state.currentBowtie._id === action.payload._id) {
                    state.currentBowtie = null
                }
            })
            .addCase(deleteBowtie.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })
            .addCase(updateBowtie.pending, (state) =>{
                state.isLoading = true
            })
            .addCase(updateBowtie.fulfilled, (state, action) => {
                const {_id, title, description, event, threatSum} = action.payload
                const existingBowtie = state.bowties.find(bowtie => bowtie._id === _id)
                state.isLoading = false
                state.isSuccess = true
                if (existingBowtie) {
                    existingBowtie.title = title
                    existingBowtie.description = description
                    existingBowtie.event = event
                    existingBowtie.threatSum = threatSum
                }
                // Also update current bowtie if it matches
                if (state.currentBowtie && state.currentBowtie._id === _id) {
                    state.currentBowtie = action.payload
                }
            })
            .addCase(updateBowtie.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })
            .addCase(moveBowtieToOrg.fulfilled, (state, action) => {
                // It left the personal workspace — drop it from the current list.
                state.bowties = state.bowties.filter((b) => b._id !== action.payload._id)
            })
            .addCase(moveBowtieToOrg.rejected, (state, action) => {
                state.isError = true
                state.message = action.payload
            })
            .addCase(cloneBowtie.rejected, (state, action) => {
                state.isError = true
                state.message = action.payload
            })
        }
    }
)

export const { reset } = bowtieSlice.actions
export default bowtieSlice.reducer